import { motion } from 'framer-motion';
import { useState } from 'react';

const PricingSection = () => {
  const [activeTab, setActiveTab] = useState<'monthly' | 'bimonthly' | 'semiannual' | 'simple'>('monthly');

  const pricingPlans = [
    {
      id: 'monthly',
      title: '毎月訪問プラン',
      frequency: '毎月お伺い・ご来社',
      monthlyFee: '50,000円',
      settlementFee: '120,000円',
      annualTotal: '720,000円',
      icon: 'ri-calendar-check-line',
      gradient: 'from-red-600 to-orange-500',
      recommended: true
    },
    {
      id: 'bimonthly',
      title: '隔月訪問プラン',
      frequency: '2ヶ月に1回お伺い・ご来社',
      monthlyFee: '40,000円',
      settlementFee: '120,000円',
      annualTotal: '600,000円',
      icon: 'ri-calendar-line',
      gradient: 'from-blue-600 to-cyan-500',
      recommended: false
    },
    {
      id: 'semiannual',
      title: '半年訪問プラン',
      frequency: '半年に1回お伺い・ご来社',
      monthlyFee: '30,000円',
      settlementFee: '120,000円',
      annualTotal: '480,000円',
      icon: 'ri-calendar-event-line',
      gradient: 'from-green-600 to-emerald-500',
      recommended: false
    },
    {
      id: 'simple',
      title: 'お手軽プラン',
      frequency: 'ネット・FAXのみ',
      monthlyFee: '0円',
      settlementFee: '120,000円',
      annualTotal: '120,000円',
      icon: 'ri-mail-line',
      gradient: 'from-purple-600 to-pink-500',
      recommended: false
    }
  ];

  const priceFactors = [
    {
      title: '訪問回数',
      icon: 'ri-map-pin-user-line',
      description: '訪問回数が多いほど料金が高くなります',
      gradient: 'from-red-500 to-orange-500'
    },
    {
      title: '相談内容の範囲',
      icon: 'ri-file-list-3-line',
      description: '所得税・法人税のみは安く、相続税・贈与税・譲渡所得税などの資産税を含む場合は高くなります',
      gradient: 'from-blue-500 to-cyan-500'
    },
    {
      title: '特殊な税務',
      icon: 'ri-global-line',
      description: '国際課税、企業再編などの内容を含む場合は高くなります',
      gradient: 'from-green-500 to-emerald-500'
    },
    {
      title: '会社の規模・複雑性',
      icon: 'ri-building-line',
      description: '有価証券・出資金保有、海外子会社保有の場合は高くなります',
      gradient: 'from-purple-500 to-pink-500'
    }
  ];

  const importantNotes = [
    {
      title: '対象範囲',
      content: '年間売上高3億円未満の法人・個人が対象です。相続税、国際課税、企業再編等の相談、年末調整、償却資産税申告作業は含みません。',
      icon: 'ri-information-line'
    },
    {
      title: '資料の不備',
      content: '資料に不備がある場合や申告期限1ヶ月前までに必要資料が揃わない場合、追加料金が発生します。粉飾修正には1日1人あたり100,000円以上の費用がかかる場合があります。',
      icon: 'ri-alert-line'
    },
    {
      title: '電子対応',
      content: '電子メール・FAXでの資料やり取りを推奨します。郵送の場合は追加料金が発生する場合があります。',
      icon: 'ri-mail-send-line'
    },
    {
      title: '別途費用項目',
      content: '事業計画書作成支援、税務調査立会い、記帳代行、会社議事録作成、税務署への調査対応などは別途料金となります。',
      icon: 'ri-file-add-line'
    },
    {
      title: 'サービス制限',
      content: '半年に1度以下の相談頻度では、納税予測サービスや税務署との交渉同行が困難になります。',
      icon: 'ri-time-line'
    },
    {
      title: '契約について',
      content: '当事務所は原則として契約書を作成します。契約内容が明確になるまでサービス提供を開始しない場合があります。',
      icon: 'ri-file-text-line'
    }
  ];

  return (
    <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-[1400px] mx-auto px-8">
        {/* セクションヘッダー */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            料金について
          </h2>
          <p className="text-gray-600 text-lg">
            お勧めコースの参考料金をご案内します
          </p>
        </motion.div>

        {/* 料金決定要因 */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-20"
        >
          <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">
            料金を決定する要因
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {priceFactors.map((factor, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <div className={`w-14 h-14 bg-gradient-to-br ${factor.gradient} rounded-xl flex items-center justify-center mb-4`}>
                  <i className={`${factor.icon} text-2xl text-white`}></i>
                </div>
                <h4 className="text-lg font-bold text-gray-900 mb-2">
                  {factor.title}
                </h4>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {factor.description}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* 料金プラン */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-20"
        >
          <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">
            参考料金プラン
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {pricingPlans.map((plan, index) => (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="relative bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 border-2 border-gray-100 hover:border-transparent overflow-hidden group"
              >
                {plan.recommended && (
                  <div className="absolute top-4 right-4 bg-red text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg">
                    おすすめ
                  </div>
                )}

                <div className={`w-16 h-16 bg-gradient-to-br ${plan.gradient} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                  <i className={`${plan.icon} text-3xl text-white`}></i>
                </div>

                <h4 className="text-xl font-bold text-gray-900 mb-2">
                  {plan.title}
                </h4>
                <p className="text-gray-600 text-sm mb-6">
                  {plan.frequency}
                </p>

                <div className="space-y-3 mb-6">
                  <div className="flex justify-between items-center pb-3 border-b border-gray-100">
                    <span className="text-gray-600 text-sm">月額顧問料</span>
                    <span className="text-lg font-bold text-gray-900">{plan.monthlyFee}</span>
                  </div>
                  <div className="flex justify-between items-center pb-3 border-b border-gray-100">
                    <span className="text-gray-600 text-sm">決算料</span>
                    <span className="text-lg font-bold text-gray-900">{plan.settlementFee}</span>
                  </div>
                </div>

                <div className={`bg-gradient-to-br ${plan.gradient} rounded-xl p-4 text-white`}>
                  <div className="text-xs mb-1 opacity-90">年間合計</div>
                  <div className="text-2xl font-bold">{plan.annualTotal}</div>
                  <div className="text-xs mt-1 opacity-75">（税抜）</div>
                </div>

                <div className={`absolute inset-0 bg-gradient-to-br ${plan.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-300 pointer-events-none`}></div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="mt-8 text-center"
          >
            <p className="text-gray-600 text-sm">
              ※ 年間売上高3億円以上の場合は、事業内容等をヒアリングの上、別途お見積りいたします
            </p>
          </motion.div>
        </motion.div>

        {/* 重要事項 */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">
            料金に関する重要事項
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            {importantNotes.map((note, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-all duration-300 border border-gray-100"
              >
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-red/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className={`${note.icon} text-xl text-red`}></i>
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-gray-900 mb-2">
                      {note.title}
                    </h4>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {note.content}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* お問い合わせCTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="text-center mt-16"
        >
          <a
            href="https://docs.google.com/forms/d/e/1FAIpQLSfcI_iqOXmdZhJO8vkCvjObLY5GxPIAQ8kXxWKDFswsKGrSRQ/viewform?usp=publish-editor"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-red text-white px-8 py-4 rounded-full font-medium hover:bg-red/90 transition-colors whitespace-nowrap cursor-pointer"
          >
            <span>お問い合わせ</span>
            <i className="ri-arrow-right-line"></i>
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default PricingSection;
